import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useCart } from "../contexts/CartContext";
import { useTranslation } from "../hooks/useTranslation";
import { getOrderStatus } from "../api/paymentService";

interface LocationState {
  orderId: string;
  paymentId: string;
  currency?: string;
}

const OrderSuccessPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { clearCart } = useCart();
  const { t } = useTranslation();
  const state = location.state as LocationState | undefined;
  
  // URL 파라미터에서 orderId 추출 (엑심베이 return_url용)
  const urlParams = new URLSearchParams(location.search);
  const urlOrderId = urlParams.get('order_id') || urlParams.get('orderId');
  
  // 상태 관리
  const [orderData, setOrderData] = useState<{
    orderId: string;
    paymentId: string;
    currency?: string;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [orderStatus, setOrderStatus] = useState<string | null>(null);

  // PayPal 방식 (기존): location.state에서 데이터 가져오기
  // Eximbay 방식 (신규): URL 파라미터에서 orderId 가져와서 API 조회
  useEffect(() => {
    const initializeOrderData = async () => {
      // 1. PayPal 방식: location.state가 있으면 기존 방식 사용
      if (location.state) {
        const { orderId, paymentId, currency } = state || {};
        if (orderId && paymentId) {
          setOrderData({ orderId, paymentId, currency });
          clearCart();
          // 주문 상태 초기 조회
          try {
            const orderInfo = await getOrderStatus(orderId);
            setOrderStatus(orderInfo.status);
          } catch (error) {
            console.error('Failed to fetch initial order status:', error);
          }
          return;
        }
      }
      
      // 2. Eximbay 방식: URL 파라미터에서 orderId가 있으면 API 조회
      if (urlOrderId) {
        setIsLoading(true);
        try {
          const orderInfo = await getOrderStatus(urlOrderId);
          setOrderData({
            orderId: orderInfo.id,
            paymentId: orderInfo.paymentId,
            currency: orderInfo.currency
          });
          setOrderStatus(orderInfo.status);
          clearCart();
        } catch (error) {
          console.error('Failed to fetch order status:', error);
          // 주문 조회 실패 시 홈으로 리다이렉트
          navigate("/", { replace: true });
          return;
        } finally {
          setIsLoading(false);
        }
        return;
      }
      
      // 3. 둘 다 없으면 홈으로 리다이렉트
      navigate("/", { replace: true });
    };

    initializeOrderData();
  }, [location.state, urlOrderId, state, navigate, clearCart]);

  // 주문 상태 폴링 (5초 간격, DELIVERY_COMPLETED 시 중단)
  useEffect(() => {
    if (!orderData?.orderId) return;
    if (orderStatus === 'DELIVERY_COMPLETED') return;

    const intervalId = setInterval(async () => {
      try {
        const orderInfo = await getOrderStatus(orderData.orderId);
        setOrderStatus(orderInfo.status);
        if (orderInfo.status === 'DELIVERY_COMPLETED') {
          clearInterval(intervalId);
        }
      } catch (error) {
        console.error('Failed to poll order status:', error);
      }
    }, 5000);

    return () => clearInterval(intervalId);
  }, [orderData?.orderId, orderStatus]);

  // 로딩 중이거나 orderData가 없으면 로딩 표시 또는 null 반환
  if (isLoading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading order details...</p>
        </div>
      </div>
    );
  }

  if (!orderData) {
    return null;
  }

  const { currency } = orderData;

  // 주문 상태 → 프로그레스 바 단계 매핑 (0-based)
  const STATUS_STEPS = [
    { key: 'orderStatus.paid', suffix: 'paid', statuses: ['PAID', 'RESERVATION_PAID'] },
    { key: 'orderStatus.accepted', suffix: 'accepted', statuses: ['BROKER_ACCEPTED'] },
    { key: 'orderStatus.cooking', suffix: 'cooking', statuses: ['COOKING'] },
    { key: 'orderStatus.delivered', suffix: 'delivered', statuses: ['DELIVERY_COMPLETED'] },
  ];

  const getActiveStep = (status: string | null): number => {
    if (!status) return 0;
    for (let i = STATUS_STEPS.length - 1; i >= 0; i--) {
      if (STATUS_STEPS[i].statuses.includes(status)) return i;
    }
    return 0;
  };

  const activeStep = getActiveStep(orderStatus);
  const activeSuffix = STATUS_STEPS[activeStep].suffix;

  return (
    <div className="min-h-screen bg-white flex flex-col items-center px-4">
      <div className="flex-1 flex flex-col items-center justify-center w-full max-w-md mx-auto">
        {/* Order Status Progress Bar */}
        <div className="w-full mb-6 pt-6">
          <div className="flex items-center justify-between relative">
            {STATUS_STEPS.map((step, index) => (
              <React.Fragment key={step.key}>
                {/* 연결선 (첫 번째 노드 제외) */}
                {index > 0 && (
                  <div className="flex-1 h-0.5 mx-1">
                    <div
                      className={`h-full transition-colors duration-300 ${
                        index <= activeStep ? 'bg-green-500' : 'bg-gray-300'
                      }`}
                    />
                  </div>
                )}
                {/* 노드 + 라벨 */}
                <div className="flex flex-col items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-colors duration-300 ${
                      index <= activeStep
                        ? 'bg-green-500 text-white'
                        : 'bg-gray-300 text-white'
                    }`}
                  >
                    {index < activeStep ? (
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      index + 1
                    )}
                  </div>
                  <span
                    className={`mt-1 text-xs whitespace-nowrap transition-colors duration-300 ${
                      index <= activeStep ? 'text-green-600 font-medium' : 'text-gray-400'
                    }`}
                  >
                    {t(step.key)}
                  </span>
                </div>
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Success Title */}
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {t(`orderSuccess.title.${activeSuffix}`)}
        </h1>

        {/* Order ID display */}
        {/* <p className="text-gray-600 mb-2">
          Order ID: <span className="font-medium text-gray-900">{orderId}</span>
        </p> */}

        {/* Payment ID display */}
        {/* <p className="text-gray-600 mb-2">
          Payment ID:{" "}
          <span className="font-medium text-gray-900">{paymentId}</span>
        </p> */}

        {/* Payment Currency display */}
        {currency && (
          <p className="text-gray-600 mb-6">
            {t('orderSuccess.paymentCurrency')}{" "}
            <span className="font-medium text-gray-900">{currency}</span>
          </p>
        )}

        {/* Order Status Info */}
        <div className="w-full bg-gray-100 rounded-lg p-6 mb-10">
          <div className="flex items-start mb-4">
            <div className="flex-shrink-0 mr-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-6 w-6 text-gray-500"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 16l2.879-2.879m0 0a3 3 0 104.243-4.242 3 3 0 00-4.243 4.242z"
                />
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <div>
              <p className="text-gray-700 font-medium">
                {t(`orderSuccess.message1.${activeSuffix}`)}
              </p>
              <p className="text-gray-700">
                {t(`orderSuccess.message2.${activeSuffix}`)}
              </p>
            </div>
          </div>

          {(activeSuffix === 'paid' || activeSuffix === 'cooking') && (
            <div className="flex items-start">
              <div className="flex-shrink-0 mr-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 text-red-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-gray-700">
                  {t(`orderSuccess.emailNotification.${activeSuffix}`)}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Buttons */}
      <div className="w-full max-w-md mx-auto mb-10">
        <button
          onClick={() => navigate("/")}
          className="w-full py-4 bg-red-500 text-white rounded-lg font-medium mb-4 hover:bg-red-600 transition-colors"
        >
          {t('orderSuccess.returnToHome')}
        </button>

        <button
          onClick={() => navigate("/browse")}
          className="w-full py-4 border border-gray-300 text-gray-800 rounded-lg font-medium hover:bg-gray-50 transition-colors"
        >
          {t('orderSuccess.exploreMore')}
        </button>
      </div>
    </div>
  );
};

export default OrderSuccessPage;
